import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { getDistance } from "geolib";
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  UserIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { useMeeting } from "../../contexts/MeetingContext";
import MeetingArrivedOverlay from "./MeetingArrivedOverlay";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const blueIcon = L.divIcon({
  className: "",
  html: `<div style="background:#3b82f6;width:20px;height:20px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const greenIcon = L.divIcon({
  className: "",
  html: `<div style="background:#22c55e;width:20px;height:20px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const fadedIcon = L.divIcon({
  className: "",
  html: `<div style="background:#94a3b8;width:20px;height:20px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);opacity:0.5"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const fetchRoute = async (start, end) => {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${start.longitude},${start.latitude};${end.longitude},${end.latitude}?geometries=geojson`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.routes?.[0]?.geometry?.coordinates) {
      return data.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]);
    }
    return [];
  } catch {
    return [];
  }
};

function MapBoundsHandler({ positions }) {
  const map = useMap();

  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map((p) => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [positions, map]);

  return null;
}

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const getStatusBadge = (status) => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";
    case "arrived":
      return "bg-blue-100 text-blue-800";
    case "offline":
      return "bg-yellow-100 text-yellow-800";
    case "ended":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const MeetingMap = () => {
  const navigate = useNavigate();
  const {
    meetingStatus,
    meeting,
    myPosition,
    otherPosition,
    error,
    endMeeting,
    leaveMeeting,
    clearError,
  } = useMeeting();

  const [routeCoords, setRouteCoords] = useState([]);
  const [duration, setDuration] = useState(0);
  const [showArrived, setShowArrived] = useState(false);

  useEffect(() => {
    let interval;
    if (meetingStatus === "active" || meetingStatus === "arrived") {
      interval = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [meetingStatus]);

  useEffect(() => {
    if (meetingStatus === "arrived") {
      setShowArrived(true);
    }
  }, [meetingStatus]);

  useEffect(() => {
    if (myPosition && otherPosition && meetingStatus === "active") {
      const now = Date.now();
      if (now - lastRouteFetch > 15000) {
        lastRouteFetch = now;
        fetchRoute(myPosition, otherPosition).then(setRouteCoords);
      }
    } else if (meetingStatus !== "active") {
      setRouteCoords([]);
    }
  }, [myPosition, otherPosition, meetingStatus]);

  const distance =
    myPosition && otherPosition
      ? getDistance(
          { latitude: myPosition.latitude, longitude: myPosition.longitude },
          { latitude: otherPosition.latitude, longitude: otherPosition.longitude }
        )
      : null;

  const handleEnd = async () => {
    await endMeeting();
  };

  const handleClose = () => {
    leaveMeeting();
    navigate(-1);
  };

  const handleRetryGPS = () => {
    clearError();
    window.location.reload();
  };

  const isLoading =
    meetingStatus === "requesting" || meetingStatus === "joining";

  const positions = [];
  if (myPosition) positions.push(myPosition);
  if (otherPosition) positions.push(otherPosition);

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-6">
        <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Meeting Error
        </h2>
        <p className="text-gray-600 mb-6 text-center">{error}</p>
        <button
          onClick={handleClose}
          className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mb-4"></div>
        <p className="text-lg text-gray-700 font-medium">Starting meeting...</p>
      </div>
    );
  }

  if (meetingStatus === "ended") {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-6">
        <CheckCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Meeting Ended
        </h2>
        <p className="text-gray-600 mb-6">The meeting has been ended.</p>
        <button
          onClick={handleClose}
          className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  if (!myPosition) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-6">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-4"></div>
        <MapPinIcon className="h-12 w-12 text-blue-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Acquiring GPS...
        </h2>
        <p className="text-gray-500 text-sm text-center">
          Please wait while we get your location
        </p>
        <button
          onClick={handleRetryGPS}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const mapCenter = [myPosition.latitude, myPosition.longitude];

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {showArrived && meetingStatus === "arrived" && (
        <MeetingArrivedOverlay
          distance={distance}
          onEnd={handleEnd}
        />
      )}

      <div className="relative flex-1">
        <MapContainer
          center={mapCenter}
          zoom={15}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {positions.length > 0 && <MapBoundsHandler positions={positions} />}

          {myPosition && (
            <Marker
              position={[myPosition.latitude, myPosition.longitude]}
              icon={blueIcon}
            >
              <Popup>You</Popup>
            </Marker>
          )}

          {otherPosition && meetingStatus !== "offline" && (
            <Marker
              position={[otherPosition.latitude, otherPosition.longitude]}
              icon={greenIcon}
            >
              <Popup>Other User</Popup>
            </Marker>
          )}

          {otherPosition && meetingStatus === "offline" && (
            <Marker
              position={[otherPosition.latitude, otherPosition.longitude]}
              icon={fadedIcon}
            >
              <Popup>User offline</Popup>
            </Marker>
          )}

          {routeCoords.length > 1 && (
            <Polyline
              positions={routeCoords}
              color="#3b82f6"
              weight={3}
              opacity={0.7}
            />
          )}
        </MapContainer>

        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/50 to-transparent p-4 z-[1000]">
          <div className="flex items-center justify-between">
            <button
              onClick={handleClose}
              className="text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-3">
              {meeting?.meetingId && (
                <span className="text-white text-sm bg-black/30 px-2 py-1 rounded">
                  #{meeting.meetingId.slice(-6)}
                </span>
              )}
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                  meetingStatus
                )}`}
              >
                {meetingStatus}
              </span>
              {(meetingStatus === "active" || meetingStatus === "arrived") && (
                <span className="text-white text-sm font-mono bg-black/30 px-2 py-1 rounded">
                  {formatDuration(duration)}
                </span>
              )}
            </div>
            <button
              onClick={handleEnd}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-3 py-1.5 rounded-md transition-colors"
            >
              End
            </button>
          </div>
        </div>

        {otherPosition === null && meetingStatus === "active" && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-lg px-4 py-2 z-[1000]">
            <p className="text-sm text-gray-700 flex items-center">
              <UserIcon className="h-4 w-4 mr-2 text-blue-500 animate-pulse" />
              Waiting for other user's location...
            </p>
          </div>
        )}

        {meetingStatus === "offline" && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-yellow-100 border border-yellow-300 shadow-lg rounded-lg px-4 py-2 z-[1000]">
            <p className="text-sm text-yellow-800 flex items-center">
              <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
              User went offline
            </p>
          </div>
        )}

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-lg px-4 py-3 z-[1000] min-w-[200px]">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-1"></div>
                <span className="text-gray-600">You</span>
              </div>
              <div className="flex items-center">
                <div
                  className={`w-3 h-3 rounded-full mr-1 ${
                    meetingStatus === "offline"
                      ? "bg-gray-400"
                      : "bg-green-500"
                  }`}
                ></div>
                <span className="text-gray-600">Other</span>
              </div>
            </div>
            {distance !== null && (
              <span className="font-medium text-gray-900">
                {distance >= 1000
                  ? `${(distance / 1000).toFixed(1)} km`
                  : `${distance} m`}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

let lastRouteFetch = 0;

export default MeetingMap;
